<template>
  <div>
    <div :class="[$style.mdmCertsContainer, $style.inputRow]">
      <CardSettings
        ref="mdmSettingsCard"
        :class="[$style.tileCard, $style.half]"
        :isFlexible="true"
        title="APNs Configuration for MDM"
      >
        <template v-slot:badge>
          <StatusBadge
            v-if="apnsCertStatusBadgeValues.message"
            :data-test-id="$testId('apnsStatusBadge')"
            :status="apnsCertStatusBadgeValues.status"
            :text="apnsCertStatusBadgeValues.message"
          />
        </template>
        <div :class="$style.mdmCertDetails">
          <ul :class="$style.mdmCertDetailsList">
            <li
              :class="$style.mdmCertDetailsListItem"
              :data-test-id="$testId('certificateUID')"
            >
              <span>Certificate UID</span>
              <span>{{ mdmDevicesValues.apnsPushTopic }}</span>
            </li>
            <li :class="$style.mdmCertDetailsListItem">
              <span>Certificate Status</span>
              <MdmCertificateStatus
                :expiresOnDate="mdmDevicesValues.apnsCertExpiry"
                noBackground
              />
            </li>
          </ul>
          <ul :class="$style.mdmCertDetailsList">
            <li
              v-if="mdmDevicesValues.appleCertSerialNumber"
              :class="$style.mdmCertDetailsListItem"
              :data-test-id="$testId('serialNumber')"
            >
              <span>Serial Number</span>
              <span>{{ mdmDevicesValues.appleCertSerialNumber }}</span>
            </li>
            <li
              v-if="mdmDevicesValues.appleCertCreatorAppleID"
              :class="$style.mdmCertDetailsListItem"
              :data-test-id="$testId('appleCertCreatorAppleID')"
            >
              <span>Apple ID</span>
              <span>{{ mdmDevicesValues.appleCertCreatorAppleID }}</span>
            </li>
          </ul>
        </div>
        <div>
          <div
            :class="$style.buttonContainer"
          >
            <Button
              :class="$style.actionButton"
              text="renew"
              @click="handleRenewMdmCertificate"
            />
            <ButtonSecondary
              :class="$style.actionButton"
              text="download profile"
              @click="handleDownloadProfile"
            />
            <ButtonSecondary
              :class="$style.actionButton"
              text="delete"
              type="error"
              @click="handleDeleteMdm"
            />
          </div>
        </div>
      </CardSettings>
      <CardSettings
        ref="depSettingsCard"
        :class="[$style.tileCard, $style.half]"
        :isFlexible="true"
        title="Automated Device Enrollment Configuration"
        :tooltip="depTooltip"
      >
        <template v-slot:badge>
          <StatusBadge
            v-if="adeCertStatusBadgeValues.message"
            :data-test-id="$testId('adeStatusBadge')"
            :status="adeCertStatusBadgeValues.status"
            :text="adeCertStatusBadgeValues.message"
          />
        </template>
        <div
          v-if="isDepServerTokenEnrolled"
          :class="$style.mdmCertDetails"
        >
          <p :class="$style.headingTitle">
            Enroll your devices in zero-touch onboarding.
          </p>
          <ul :class="$style.mdmCertDetailsList">
            <li :class="$style.mdmCertDetailsListItem">
              <span>Token State</span>
              <span :class="$style.capitalize">{{ getDepTokenState || '' }}</span>
            </li>
            <li :class="$style.mdmCertDetailsListItem">
              <span>Token Status</span>
              <MdmCertificateStatus
                :expiresOnDate="getDepTokenExpiry"
                noBackground
              />
            </li>
          </ul>
        </div>
        <p
          v-else
        >
          Connect your organization to Apple's Device Enrollment Program to enroll
          {{ deviceItem.labelPlural }} into JumpCloud's MDM through
          Automated Device Enrollment. An account with
          <a
            href="https://support.apple.com/guide/apple-business-manager/enroll-in-apple-business-manager-apd402206497/web"
            rel="noopener noreferrer"
            target="_blank"
          >Apple Business Manager</a> or
          <a
            href="https://support.apple.com/guide/apple-school-manager/enroll-in-apple-school-manager-apd402206497/web"
            rel="noopener noreferrer"
            target="_blank"
          >Apple School Manager</a> is required to configure this.
        </p>
        <div
          v-if="!isDepServerTokenEnrolled"
          :class="$style.buttonContainer"
        >
          <FlatActionButton
            :action="handleGetStarted"
            :buttonClass="['btnDefault', $style.actionButton]"
            text="get started"
          />
        </div>
        <div
          v-else
          :class="$style.buttonContainer"
        >
          <Button
            :class="$style.actionButton"
            text="Renew"
            @click="handleRenewDepToken"
          />
          <ButtonSecondary
            :class="$style.actionButton"
            text="Configure macOS"
            @click="handleMacosSetupExperience"
          />
          <ButtonSecondary
            :class="$style.actionButton"
            text="Configure iOS and iPadOS"
            @click="handleIosSetupExperience"
          />
        </div>
      </CardSettings>
    </div>
    <div
      v-if="mdmDevicesValues"
      :class="[$style.inputRow, $style.MdmIOSUserEnrollmentContainer]"
    >
      <CardSettings
        ref="mdmIOSUserEnrollmentCard"
        :class="$style.tileCard"
        :isFlexible="true"
        title="User iOS Configuration"
        :tooltip="iOSUserEnrollmentTooltip"
      >
        <MdmIOSUserEnrollment />
      </CardSettings>
    </div>
    <div :class="$style.iOSEnrollmentContainer">
      <CardSettings
        ref="adminIosConfigurationCard"
        :class="$style.tileCard"
        :isFlexible="true"
        title="Admin iOS Configuration"
        :tooltip="iOSTooltip"
      >
        <MDMAdminIOSConfiguration />
      </CardSettings>
    </div>
    <div
      v-if="isDepServerTokenEnrolled"
      :class="$style.depDevices"
    >
      <h3 :class="$style.depDevicesTitle">
        Automated Device Enrollment Devices
      </h3>
      <div :class="$style.depDevicesIntro">
        <p :class="$style.depDevicesIntroText">
          These devices are registered under Apple’s Business Manager.
          To manage them with JumpCloud install the JumpCloud agent.
          If you have recently purchased devices from ABM click
          <strong>sync with Apple</strong> to import them into JumpCloud.
          <a
            :href="mdmDepSyncLink"
            rel="noopener noreferrer"
            target="_blank"
          >Learn More.</a>
        </p>
        <FlatActionButton
          :action="handleRefreshDevices"
          :disabled="disableSyncMdmSystems"
          text="sync with Apple"
        />
      </div>
      <div :class="$style.tableWithPagination">
        <LoadingOverlay :showLoadingOverlay="loading" />
        <NoRecordsFound v-show="!loading && !totalCount" />
        <TableWithPagination
          v-show="totalCount"
          :columns="columns"
          :currentPageNum="currentPageNum"
          :footerDetails="deviceItem.labelPlural"
          :rowsPerPage="rowsPerPage"
          :tableData="tableData"
          :totalCount="totalCount"
          @pageChange="handlePageChange"
        />
      </div>
    </div>
    <SeverityModal
      ref="deleteMdmModal"
      :isMarkdown="true"
      :notificationMessage="deleteMdmNotificationMessage"
      :notificationTitle="deleteMdmNotificationTitle"
      :numberAffectedProp="numMdmDevices"
      :primaryButtonProps="primaryButtonProps"
      :showModal="showDeleteMdmModal"
      :title="deleteMdmModalTitle"
      useInputNumber
      variant="sev3"
      @close="handleCancelDeleteMdm"
      @save="handleConfirmDeleteMdm"
    >
      <template v-slot:subtitle>
        <span>Are you sure you want to delete your existing MDM configuration?</span>
      </template>
      <template v-slot:learnmore>
        <ParagraphBody
          :class="$style.deleteMdmText"
        >
          <p>For more information, visit </p>
          <Link
            :href="manageAppleDevicesKb.kbLink"
            :style="manageAppleDevicesKb.style"
            target="_blank"
            :text="manageAppleDevicesKb.text"
          />
          <p> or email</p>
          <Link
            :href="supportKb.kbLink"
            :style="supportKb.style"
            target="_blank"
            :text="supportKb.text"
          />
        </ParagraphBody>
      </template>
      <template v-slot:list>
        <ParagraphBody>
          To proceed, enter the number of devices that will be deleted from your MDM management.
        </ParagraphBody>
      </template>
    </SeverityModal>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import {
  Button,
  ButtonSecondary,
  Link,
  ParagraphBody,
  SeverityModal,
  StatusBadge,
  Types,
} from '@jumpcloud/ui-components';
import { featureFlags } from 'client/store/modules/FeatureFlagsModule';
import { isEmpty } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notify } from 'client/util';
import CardSettings from 'client/components/CardSettings.vue';
import DateUtil from 'client/util/DateUtil';
import FlatActionButton from 'components/Buttons/FlatActionButton.vue';
import ItemType from 'client/util/Constants/ItemType';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import LoadingOverlay from 'components/LoadingOverlay/LoadingOverlay.vue';
import MDMAdminIOSConfiguration from 'client/MDM/MdmAdminIOSConfiguration.vue';
import MdmCertificateStatus from 'client/MDM/components/MdmCertificateStatus.vue';
import MdmIOSUserEnrollment from 'client/MDM/MdmIOSUserEnrollment.vue';
import MdmSettingsMixin from 'client/MDM/mixins/MdmSettingsMixin';
import NoRecordsFound from 'client/components/Tables/NoRecordsFound.vue';
import TableWithPagination from 'components/Tables/TableWithPagination.vue';
import TooltipTextConstants from 'client/util/Constants/ToolTipTextConstants';
import Util from 'client/util/Util';

const { DeviceItemType } = ItemType;
const deviceItem = new DeviceItemType();
const { PrimaryButtonProps } = Types;

const {
  ItemTypeConsts,
  StoreModelMap,
  StoreModuleMap,
} = Constants;
const { success, error } = Constants.default;
const { mdmIOSUserEnrollment } = featureFlags;
const mdmSettingsModel = StoreModelMap[ItemTypeConsts.mdmSettings];
const systemCollection = StoreModuleMap[ItemTypeConsts.system];

export default {
  name: 'MdmSettings',

  components: {
    Button,
    ButtonSecondary,
    CardSettings,
    FlatActionButton,
    Link,
    LoadingOverlay,
    MDMAdminIOSConfiguration,
    MdmCertificateStatus,
    MdmIOSUserEnrollment,
    NoRecordsFound,
    StatusBadge,
    ParagraphBody,
    SeverityModal,
    TableWithPagination,
  },

  mixins: [MdmSettingsMixin],

  routes: {
    configureMacos: '/#/mdm/mac',
    configureIos: '/#/mdm/ios',
  },

  data: () => ({
    columns: [],
    currentPageNum: 0,
    depTooltip: {
      text: 'Learn more about the JumpCloud Automated Device Enrollment functionality.',
      kbLink: KbArticleUrls.mdmDepIntegration,
    },
    deleteMdmModalTitle: 'Delete MDM Configuration',
    deleteMdmModalSubtitle: 'Are you sure you want to delete your existing MDM configuration?',
    deleteMdmNotificationTitle: 'Deleting an MDM Configuration',
    deviceItem,
    disableSyncMdmSystems: false,
    iOSTooltip: {
      text: TooltipTextConstants.mdmIOSAdminEnrollment,
      kbLink: KbArticleUrls.enrolllingIosDevicesInMdm,
    },
    iOSUserEnrollmentTooltip: {
      text: TooltipTextConstants.mdmIOSUserEnrollment,
      kbLink: KbArticleUrls.mdmIOSUserEnrollment,
    },
    loading: false,
    manageAppleDevicesKb: {
      text: 'Manage Apple Devices with JumpCloud',
      kbLink: KbArticleUrls.mdmManageDevices,
      style: { display: 'contents' },
    },
    numMdmDevices: 0,
    rowsPerPage: 10,
    tableData: [],
    totalCount: 0,
    mdmDepSyncLink: KbArticleUrls.mdmDepConfigure,
    primaryButtonProps: PrimaryButtonProps({
      isLoading: false,
      isDisabled: false,
      hasOutline: true,
      text: 'Delete MDM Configuration',
      type: 'error',
    }),
    showDeleteMdmModal: false,
    supportKb: {
      text: 'Support.',
      kbLink: KbArticleUrls.supportContact,
      style: { display: 'contents' },
    },
  }),

  computed: {
    ...mapState('FeatureFlagsModule', {
      [mdmIOSUserEnrollment]: state => state.betaFeatures.mdmIOSUserEnrollment,
    }),
    ...mapState(mdmSettingsModel, ['item']),
    ...mapGetters(mdmSettingsModel, [
      'getDepTokenExpiry',
      'getDepTokenState',
    ]),

    adeCertStatusBadgeValues() {
      let message = '';
      let status = '';
      const daysUntilExpiry = this.daysUntilExpiry(this.getDepTokenExpiry);

      if (this.isExpiringSoon(daysUntilExpiry)) {
        message = 'Expiration Warning';
        status = 'warning';
      }

      if (this.isExpired(daysUntilExpiry)) {
        message = 'Token Expired';
        status = 'error';
      }

      return {
        message,
        status,
      };
    },

    apnsCertStatusBadgeValues() {
      let message = '';
      let status = '';
      const daysUntilExpiry = this.daysUntilExpiry(this.mdmDevicesValues.apnsCertExpiry);

      if (this.isExpiringSoon(daysUntilExpiry)) {
        message = 'Expiration Warning';
        status = 'warning';
      }

      if (this.isExpired(daysUntilExpiry)) {
        message = 'Certificate Expired';
        status = 'error';
      }

      return {
        message,
        status,
      };
    },

    deleteMdmNotificationMessage() {
      return `You are about to reset your JumpCloud MDM Configuration. This action cannot be undone.

    • This will remove all existing macOS and iOS devices from MDM management.

    • Any device you wish to manage in the future will need to be reenrolled with MDM after you reconfigure MDM.

    • You will no longer have access to **${this.numMdmDevices}** macOS and iOS devices.
    `;
    },

    isDepServerTokenEnrolled() {
      const { item: { mdmDevices } } = this;

      if (isEmpty(mdmDevices)) return '';

      return mdmDevices[0].depServerTokenState === 'valid';
    },

    mdmDevicesValues() {
      const { item: { mdmDevices } } = this;

      if (isEmpty(mdmDevices)) return '';
      return {
        apnsPushTopic: mdmDevices[0].apnsPushTopic,
        apnsCertExpiry: mdmDevices[0].apnsCertExpiry,
        appleCertCreatorAppleID: mdmDevices[0].appleCertCreatorAppleID,
        appleCertSerialNumber: mdmDevices[0].appleCertSerialNumber,
      };
    },
  },

  watch: {
    getDmId(value) {
      if (value) {
        this.setEnrollmentProfiles();
      }
    },
    isDepServerTokenEnrolled(value) {
      if (value) {
        this.handleRefreshDevices(false);
      }
    },
  },

  async created() {
    if (this.isDepServerTokenEnrolled) this.handleRefreshDevices(false);

    await this.setEnrollmentProfiles();
  },

  methods: {
    ...mapActions(mdmSettingsModel, [
      'deleteMdmConfiguration',
      'fetchDepDevices',
      'refreshDepDevices',
    ]),

    ...mapActions(systemCollection, [
      'fetchTotalInternalMdmDeviceCount',
    ]),

    daysUntilExpiry(expiryDate) {
      return DateUtil.daysUntil(expiryDate);
    },

    handleCancelDeleteMdm() {
      this.showDeleteMdmModal = false;
    },

    handleConfirmDeleteMdm() {
      this.handleDeleteMdmConfig();
    },

    async handleDeleteMdm() {
      this.showDeleteMdmModal = true;
      this.numMdmDevices = await this.fetchTotalInternalMdmDeviceCount();
    },

    handleDeleteMdmConfig() {
      this.deleteMdmConfiguration()
        .then(() => {
          notify(success, 'JumpCloud MDM Configuration deleted');
          this.$emit('mdm-delete');
        })
        .catch(() => {
          notify(error, 'JumpCloud MDM Configuration unable to be deleted');
        })
        .finally(() => {
          this.showDeleteMdmModal = false;
        });
    },

    handleGetStarted() {
      this.$emit('dep-setup');
    },

    handleIosSetupExperience() {
      Util.redirect(this.$options.routes.configureIos);
    },

    handleMacosSetupExperience() {
      Util.redirect(this.$options.routes.configureMacos);
    },

    handlePageChange({ currentPageNum, rowsPerPage }) {
      this.currentPageNum = currentPageNum;
      this.rowsPerPage = rowsPerPage;
      this.refreshData();
    },

    /**
     * This function is invoked every time we load the MDM page.
     * But the success notification should only be shown when the user
     * clicks on the Sync with Apple button
     * @param {boolean} showNotification
     */
    handleRefreshDevices(showNotification = true) {
      this.disableSyncMdmSystems = true;

      this.refreshDepDevices()
        .then(() => {
          if (showNotification) {
            notify(success, `JumpCloud’s list of Automated Device Enrollment ${deviceItem.labelPlural} now matches what is in Apple Business Manager. These ${deviceItem.labelPlural} will automatically enroll in JumpCloud MDM and the JumpCloud system agent will be installed during Automated Device Enrollment.`);
          }
          this.refreshData();
          this.currentPageNum = 0;
        }).catch(() => {
          notify(error, `JumpCloud’s list of Automated Device Enrollment ${deviceItem.labelPlural} could not be updated to match your Apple Business Manager list. Please try again.`);
        }).finally(() => {
          this.disableSyncMdmSystems = false;
        });
    },

    handleRenewDepToken() {
      this.$emit('dep-renew');
    },

    handleRenewMdmCertificate() {
      this.$emit('renew-cert');
    },

    isExpired(daysUntilExpiry) {
      return daysUntilExpiry <= 0;
    },

    isExpiringSoon(daysUntilExpiry) {
      return daysUntilExpiry > 0 && daysUntilExpiry <= 60;
    },

    async refreshData() {
      this.loading = true;
      try {
        const { data, headers } = await this.fetchDepDevices({
          limit: this.rowsPerPage,
          skip: this.currentPageNum * this.rowsPerPage,
        });
        this.columns = [
          { dataField: 'serialNumber', name: 'Serial Number' },
          { dataField: 'createdOn', name: 'Created on' },
          { dataField: 'modelName', name: 'Model' },
        ];
        this.tableData = data.map(device => ({
          serialNumber: device.serialNumber,
          createdOn: device.createdAt,
          modelName: device.deviceInformation.modelName,
        }));
        this.totalCount = parseInt(headers['x-total-count'], 10);
      } catch (exp) {
        notify(error, `JumpCloud's list of Automated Device Enrollment ${deviceItem.label} could not be updated to match your Apple Business Manager list. Please try again.`);
      } finally {
        this.loading = false;
      }
    },

  },
};
</script>

<style module>
@import '../css/flex.css';

@value (max-width-x-large) from '~css/breakpoints.css';
@value (jcPrimary, jcGrey100, jcGrey200) from '~css/brand-colors.css';

.actionButton {
  margin-right: 0.5rem;
}

.capitalize {
  text-transform: capitalize;
}

.dateExpire {
  display: inline-block;
  font-size: 11px;
  padding-left: 5px;
  vertical-align: text-top;
}

.deleteMdmText {
  align-items: center;
  display: flex;
}

.deleteMdmText > * {
  display: contents;
}

.depDevices {
  background-color: white;
  border: 1px solid jcGrey200;
  border-radius: 5px;
  margin-top: 20px;
  padding: 5px 20px;
}

.depDevices .depDevicesTitle {
  font-size: 1.3rem;
  font-weight: 700;
}

.headingTitle {
  margin-top: 1rem;
}

.tileCard {
  margin-top: 20px;
}

.mdmCertsContainer {
  align-items: stretch;
  display: flex;
  flex-direction: row;
  min-height: 230px;
}

.mdmCertDetails {
  border-top: 1px solid jcGrey100;
  padding-bottom: 25px;
}

.mdmCertDetailsList {
  display: flex;
  justify-content: space-between;
  list-style-type: none;
  padding: 20px 0 0;
}

.mdmCertDetailsListItem {
  display: flex;
  flex-basis: 49%;
  flex-direction: column;
}

.mdmCertDetailsListItem > span:first-child {
  font-weight: bold;
  padding-bottom: 3px;
}

.mdmCertDetailsListItem > span:last-child {
  font-size: 13px;
}

.MdmIOSUserEnrollmentContainer {
  margin-bottom: 3rem;
  margin-top: 3rem;
}

.iOSEnrollmentContainer {
  margin-bottom: 3rem;
  margin-top: 3rem;
}

.buttonContainer {
  display: flex;
}

.tableWithPagination {
  margin: 30px 0;
}

.mdmCertDetails .qrCodeButton {
  background-color: jcPrimary;
  color: black;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1rem;
}

@media (max-width: 900px) {
  .buttonContainer {
    flex-direction: column;
  }

  .actionButton {
    margin: 0.5rem;
  }

  .mdmCertDetailsList {
    flex-direction: column;
    word-break: break-all;
  }
}

@media (max-width: max-width-x-large) {
  .mdmCertDetailsList {
    flex-direction: column;
  }

  .mdmCertDetailsListItem:first-child {
    margin-bottom: 5px;
  }
}
</style>
